import { chiCaption } from "../../shared/chi_text";
import { ReferenceEntry } from "../../shared/types";
import { CHANNELS } from "../../shared/channels";
import { call, mediaSrc } from "../bridge";
import { byId, flashToast } from "../dom";
import { onViewHidden, onViewShown } from "../navigation";
import { buzz } from "../tweens";
import { iconSvg } from "../icons";
import { debounce, matchesSearch } from "../utils";
import { loadRefs, refsNow } from "../refs_store";
import { t } from "../i18n";

// timed gesture drawing from the ref library. each pose drops in, counts down, then the next one

interface Pose {
  id: string;
  url: string;
}

let source: "library" | "refs" = "library";
let seconds = 60;
let count = 10;
let queue: Pose[] = [];
let fetching = false;
let step = 0;
let endAt = 0;
let left = 0;
let timer = 0;
let paused = false;

function stills(): ReferenceEntry[] {
  const filter = byId<HTMLInputElement>("gesture-filter").value;
  return refsNow().filter((entry) => {
    if (entry.status && entry.status !== "saved") return false;
    if (entry.kind === "video" || /\.(mp4|webm|mov|m4v)$/i.test(entry.fileName || "")) return false;
    return matchesSearch(filter, entry.author, entry.note || "", chiCaption(entry), (entry.tags || []).join(" "));
  });
}

function refPoses(): Pose[] {
  return shuffle(stills()).map((entry) => ({ id: entry.id, url: entry.fileUrl }));
}

function paintPool(): void {
  byId("gesture-filter-wrap").hidden = source !== "refs";
  if (source === "library") {
    byId("gesture-pool").textContent = t("random full body poses from the pocketbase library");
    byId<HTMLButtonElement>("gesture-start").disabled = false;
    return;
  }
  const pool = stills().length;
  byId("gesture-pool").textContent = pool ? t("{n} refs in the pool", { n: pool }) : t("no refs match yet");
  byId<HTMLButtonElement>("gesture-start").disabled = pool === 0;
}

async function libraryPoses(howMany: number): Promise<Pose[]> {
  return call<Pose[]>(CHANNELS.gesturePick, howMany);
}

// keep a few poses ahead so the next one is always loaded
async function topUp(): Promise<void> {
  if (fetching || (count && queue.length >= count)) return;
  if (queue.length - step > 4) return;
  fetching = true;
  try {
    const more = source === "library" ? await libraryPoses(count ? Math.min(10, count - queue.length) : 10) : refPoses();
    queue = [...queue, ...more];
    const next = queue[step + 1];
    if (next) new Image().src = mediaSrc(next.url);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not fetch poses"));
  } finally {
    fetching = false;
  }
}

function shuffle<T>(items: T[]): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function clock(total: number): string {
  const safe = Math.max(0, total);
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

function paintHud(): void {
  byId("gesture-step").textContent = count ? `${step + 1}/${count}` : `${step + 1}`;
  byId("gesture-clock").textContent = clock(Math.ceil(left / 1000));
  byId("gesture-progress").style.transform = `scaleX(${Math.max(0, left / (seconds * 1000))})`;
}

function showPose(): void {
  if (count && step >= count) return finish();
  const entry = queue[step];
  if (!entry) {
    void topUp().then(() => (queue[step] ? showPose() : finish()));
    return;
  }
  const image = byId<HTMLImageElement>("gesture-image");
  image.style.animation = "none";
  void image.offsetWidth;
  image.style.animation = "";
  image.src = mediaSrc(entry.url);
  left = seconds * 1000;
  endAt = Date.now() + left;
  paintHud();
  // warm the next picture so the swap is instant
  const next = queue[step + 1];
  if (next) new Image().src = mediaSrc(next.url);
  void topUp();
}

function tick(): void {
  if (paused) return;
  left = endAt - Date.now();
  if (left <= 0) {
    buzz(40);
    step += 1;
    showPose();
    return;
  }
  paintHud();
}

function setPaused(next: boolean): void {
  paused = next;
  if (!paused) endAt = Date.now() + left;
  byId("gesture-stage").classList.toggle("paused", paused);
  byId("gesture-pause").innerHTML = `${iconSvg(paused ? "play" : "pause", 20)}<small>${paused ? t("play") : t("pause")}</small>`;
}

async function start(): Promise<void> {
  const button = byId<HTMLButtonElement>("gesture-start");
  button.disabled = true;
  queue = [];
  step = 0;
  try {
    queue = source === "library" ? await libraryPoses(count ? Math.min(10, count) : 10) : refPoses();
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not fetch poses"));
  } finally {
    button.disabled = false;
  }
  if (!queue.length) return flashToast(t("no poses to draw from"));
  paused = false;
  byId("gesture-setup").hidden = true;
  byId("gesture-stage").hidden = false;
  byId("gesture-stage").classList.remove("paused");
  showPose();
  window.clearInterval(timer);
  timer = window.setInterval(tick, 200);
}

function stop(): void {
  window.clearInterval(timer);
  timer = 0;
  byId("gesture-stage").hidden = true;
  byId("gesture-setup").hidden = false;
}

function finish(): void {
  const done = count ? count : step;
  stop();
  flashToast(t("{n} poses done, nice", { n: done }));
}

function pickSeg(id: string, apply: (value: number) => void): void {
  byId(id).addEventListener("click", (event) => {
    const option = (event.target as Element).closest<HTMLButtonElement>("[data-value]");
    if (!option) return;
    apply(Number(option.dataset.value));
    byId(id).querySelectorAll(".seg-option").forEach((item) => item.classList.toggle("active", item === option));
  });
}

export function setupGestureView(): void {
  byId("gesture-source").addEventListener("click", (event) => {
    const option = (event.target as Element).closest<HTMLButtonElement>("[data-value]");
    if (!option) return;
    source = option.dataset.value === "refs" ? "refs" : "library";
    byId("gesture-source").querySelectorAll(".seg-option").forEach((item) => item.classList.toggle("active", item === option));
    paintPool();
  });
  pickSeg("gesture-seconds", (value) => (seconds = value || 60));
  pickSeg("gesture-count", (value) => (count = value));
  byId("gesture-filter").addEventListener("input", debounce(paintPool, 150));
  byId("gesture-start").addEventListener("click", () => void start());
  byId("gesture-stop").addEventListener("click", stop);
  byId("gesture-pause").addEventListener("click", () => setPaused(!paused));
  byId("gesture-skip").addEventListener("click", () => {
    step += 1;
    showPose();
  });
  window.addEventListener("keydown", (event) => {
    if (!timer) return;
    if (event.key === "Escape") stop();
    else if (event.key === " ") {
      event.preventDefault();
      setPaused(!paused);
    } else if (event.key === "ArrowRight") {
      step += 1;
      showPose();
    }
  });
  onViewShown("gesture", () => {
    paintPool();
    void loadRefs().then(paintPool).catch(() => undefined);
  });
  onViewHidden("gesture", stop);
}
